// Client for the domain_manager Azure Functions service.
// Calls go to sm4rt-api's staff-authenticated passthrough (/api/domain-mgr/* ->
// domain_manager /api/*), which adds the function key server-side. Dev: the CRA
// '/api' proxy sends this to sm4rt-api on :7071. The two batch-job triggers accept
// a { dry_run } body; the owner audit is a read-only GET. All three return a
// { message, result } summary.

const API_BASE_URL = '/api/domain-mgr';

/** A single field-level change within a detail row (used by sync). */
export interface ChangeField {
  field: string;
  from: unknown;
  to: unknown;
}

/** One per-domain change/action row backing the filterable detail table. */
export interface DetailRow {
  action: string;
  domain: string;
  changes?: ChangeField[]; // sync: field diffs
  detail?: string; // manage: free-text note (e.g. "first reminder")
  domainId?: string; // manage 'without_owner' / audit rows: cr001_domainsid, for inline owner assignment
}

/** Summary returned by POST /api/sync (Synergy -> Dataverse reconciliation). */
export interface SyncSummary {
  dry_run: boolean;
  vendor_count: number;
  internal_count: number;
  skipped_vendor_rows: number;
  would_upsert: number;
  would_deactivate: number;
  upserted: number;
  deactivated: number;
  details?: DetailRow[];
  details_truncated?: boolean;
}

/** Summary returned by POST /api/manage (renewal billing lifecycle). */
export interface ManageSummary {
  dry_run: boolean;
  expiring_domains: number;
  evaluated: number;
  without_owner: number;
  techsolvers_skipped: number;
  techsolvers_flag_updated: number;
  lapsing_skipped: number;
  notifications: number;
  schedule_items_created: number;
  invoices_created: number;
  autorenew_disabled: number;
  autorenew_enabled: number;
  details?: DetailRow[];
  details_truncated?: boolean;
}

/**
 * Summary returned by GET /api/owner-audit — a read-only comparison of the two
 * ownership sources, ahead of migrating off the RepairShopr-sourced customer
 * cr001_domains list. `safe_to_cut_over` is true once the domain-row lookup names an
 * owner everywhere the RS list does and the two never disagree. Domains we no longer
 * hold are excluded from every count except `inactive_skipped`.
 */
export interface OwnerAuditSummary {
  domain_count: number;
  inactive_skipped: number;
  active_count: number;
  lookup_owner: number;
  string_owner: number;
  agree: number;
  disagree: number;
  lookup_only: number;
  string_only: number;
  no_owner: number;
  techsolvers_by_lookup: number;
  techsolvers_by_string: number;
  lookup_coverage_pct: number;
  safe_to_cut_over: boolean;
  details?: DetailRow[];
  details_truncated?: boolean;
}

export interface DomainManagerResponse<T> {
  message: string;
  result: T;
}

async function postTrigger<T>(
  path: string,
  dryRun: boolean,
  signal?: AbortSignal,
): Promise<DomainManagerResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dry_run: dryRun }),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  return response.json();
}

async function getSummary<T>(
  path: string,
  signal?: AbortSignal,
): Promise<DomainManagerResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${path}`, { signal });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  return response.json();
}

export function triggerDomainSync(
  dryRun: boolean,
  signal?: AbortSignal,
): Promise<DomainManagerResponse<SyncSummary>> {
  return postTrigger<SyncSummary>('/sync', dryRun, signal);
}

export function triggerDomainManage(
  dryRun: boolean,
  signal?: AbortSignal,
): Promise<DomainManagerResponse<ManageSummary>> {
  return postTrigger<ManageSummary>('/manage', dryRun, signal);
}

export function fetchOwnerAudit(
  signal?: AbortSignal,
): Promise<DomainManagerResponse<OwnerAuditSummary>> {
  return getSummary<OwnerAuditSummary>('/owner-audit', signal);
}
